import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Car, Upload, Image } from "lucide-react";

interface ServiceType { id: string; name: string; }

export function VehiclesSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [form, setForm] = useState({
    service_type_id: "", name: "", min_passengers: "1", capacity: "4",
    image_url: "", description: "", features: "", is_active: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const load = async () => {
    const [{ data: vehicles }, { data: types }] = await Promise.all([
      supabase.from("vehicles").select("*").order("sort_order"),
      supabase.from("service_types").select("id, name").order("sort_order"),
    ]);
    setRows(vehicles || []);
    setServiceTypes(types || []);
  };

  useEffect(() => { load(); }, []);

  const getServiceTypeName = (id: string | null) => serviceTypes.find(st => st.id === id)?.name || "—";

  const handleSave = async () => {
    const featuresArray = form.features.split(",").map(f => f.trim()).filter(Boolean);
    const payload: any = {
      name: form.name,
      description: form.description || null,
      capacity: Number(form.capacity),
      min_passengers: Number(form.min_passengers),
      image_url: form.image_url || null,
      features: featuresArray.length > 0 ? featuresArray : null,
      service_type_id: form.service_type_id || null,
      is_active: form.is_active,
    };
    if (editingId) {
      const { error } = await supabase.from("vehicles").update(payload).eq("id", editingId);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    } else {
      const { error } = await supabase.from("vehicles").insert(payload);
      if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    }
    resetForm();
    load();
    toast({ title: editingId ? "Actualizado" : "Creado" });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from("vehicles").delete().eq("id", deleteId);
    setDeleteId(null);
    load();
  };

  const resetForm = () => {
    setForm({ service_type_id: "", name: "", min_passengers: "1", capacity: "4", image_url: "", description: "", features: "", is_active: true });
    setEditingId(null);
    setIsDialogOpen(false);
  };

  const openCreate = () => { resetForm(); setIsDialogOpen(true); };
  const openEdit = (row: any) => {
    setForm({
      service_type_id: row.service_type_id || "",
      name: row.name,
      min_passengers: String(row.min_passengers ?? 1),
      capacity: String(row.capacity),
      image_url: row.image_url || "",
      description: row.description || "",
      features: row.features?.join(", ") || "",
      is_active: row.is_active,
    });
    setEditingId(row.id);
    setIsDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Vehículos</h2>
          <p className="text-muted-foreground text-sm mt-1">Flota de vehículos <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Car className="h-10 w-10 mb-3 opacity-40" /><p>No hay vehículos registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo de Servicio</TableHead>
                  <TableHead>Pasajeros</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{getServiceTypeName(row.service_type_id)}</TableCell>
                    <TableCell>{row.min_passengers ?? 1}–{row.capacity} pax</TableCell>
                    <TableCell>
                      <Badge className={row.is_active ? "bg-green-500/15 text-green-700 border-green-200" : "bg-red-500/15 text-red-700 border-red-200"} variant="outline">
                        {row.is_active ? "Activo" : "Inactivo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Editar Vehículo" : "Nuevo Vehículo"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Servicio</Label>
                <Select value={form.service_type_id} onValueChange={v => setForm({ ...form, service_type_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {serviceTypes.map(st => (
                      <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nombre del Vehículo</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Mín. Pasajeros</Label><Input type="number" min="1" value={form.min_passengers} onChange={e => setForm({ ...form, min_passengers: e.target.value })} /></div>
              <div><Label>Máx. Pasajeros</Label><Input type="number" min="1" value={form.capacity} onChange={e => setForm({ ...form, capacity: e.target.value })} /></div>
            </div>
            <div>
              <Label>Imagen del Vehículo</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.image_url}
                  onChange={e => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://... o sube un archivo"
                  className="flex-1"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    const ext = file.name.split(".").pop();
                    const path = `${crypto.randomUUID()}.${ext}`;
                    const { error } = await supabase.storage.from("vehicle-images").upload(path, file);
                    if (error) {
                      toast({ title: "Error al subir", description: error.message, variant: "destructive" });
                      setUploading(false);
                      return;
                    }
                    const { data: urlData } = supabase.storage.from("vehicle-images").getPublicUrl(path);
                    setForm(prev => ({ ...prev, image_url: urlData.publicUrl }));
                    setUploading(false);
                    toast({ title: "Imagen subida" });
                  }}
                />
                <Button type="button" variant="outline" size="icon" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4" />
                </Button>
              </div>
              {form.image_url && (
                <div className="mt-2 rounded-md border overflow-hidden w-20 h-14">
                  <img src={form.image_url} alt="Preview" className="w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div><Label>Descripción</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div><Label>Características (separadas por comas)</Label><Textarea value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} placeholder="WiFi, A/C, Agua embotellada" rows={2} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              <Label>Activo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar vehículo?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
